<template>
    <div>
        <BaseTabs
            v-model="activeDimension"
            :tab-ids="tabIds"
            :labels="tabLabels"
            indicator-class="hidden"
            trigger-class="rounded-full border p-3 body-small-semibold data-[state=active]:text-black data-[state=inactive]:opacity-50 transition-[colors,opacity] flex-none whitespace-nowrap bg-white"
            list-class="shadow-none gap-4"
            content-class="mt-2">
            <template v-for="id in tabIds" :key="id" #[`content-${id}`]>
                <DimensionLenses v-if="dimensionProps[id]" :dimension="dimensionProps[id]" />
            </template>
        </BaseTabs>
    </div>
</template>

<script setup lang="ts">
import decisionMakingIcon from "~img/perspective/decision-making.svg";
import energyIcon from "~img/perspective/energy.svg";
import infoProcessingIcon from "~img/perspective/information-processing.svg";
import organizationIcon from "~img/perspective/organization.svg";
import type { PerspectiveReportDimension } from "~vue/appTypes";
import BaseTabs from "~vue/components/BaseTabs.vue";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { ref, watch } from "vue";

import DimensionLenses from "./DimensionLenses.vue";

enum TabIds {
    ENERGY = "energy",
    INFORMATION_PROCESSING = "information_processing",
    DECISION_MAKING = "decision_making",
    ORGANIZATION = "organization",
}

const tabs = {
    [TabIds.ENERGY]: { label: "Energy and Communication", icon: energyIcon, letters: "EI" },
    [TabIds.INFORMATION_PROCESSING]: { label: "Understanding", icon: infoProcessingIcon, letters: "SN" },
    [TabIds.DECISION_MAKING]: { label: "Decision Making", icon: decisionMakingIcon, letters: "TF" },
    [TabIds.ORGANIZATION]: { label: "Organization", icon: organizationIcon, letters: "JP" },
};

type DimensionSectionProps = {
    dimensions: PerspectiveReportDimension[];
};

const { dimensions } = defineProps<DimensionSectionProps>();

const dimensionProps = {
    [TabIds.ENERGY]: dimensions.find((dim) => dim.dimension === tabs[TabIds.ENERGY].letters),
    [TabIds.INFORMATION_PROCESSING]: dimensions.find((dim) => dim.dimension === tabs[TabIds.INFORMATION_PROCESSING].letters),
    [TabIds.DECISION_MAKING]: dimensions.find((dim) => dim.dimension === tabs[TabIds.DECISION_MAKING].letters),
    [TabIds.ORGANIZATION]: dimensions.find((dim) => dim.dimension === tabs[TabIds.ORGANIZATION].letters),
};

const tabIds = Object.keys(tabs) as TabIds[];
const tabLabels = Object.fromEntries(tabIds.map((id) => [id, tabs[id].label]));

const activeDimension = ref<TabIds>(tabIds[0]);
watch(activeDimension, (dimension) => {
    void logUserInteraction(AmplitudeEventNames.PRIVATE_PROFILE_TAB_CLICKED, { tab_type: "dimension", tab: dimension });
});
</script>

<style scoped>
@reference "../../../css/tailwind.css";
</style>
